/**
 * API utility functions with automatic 401 handling
 * Uses httpOnly cookies for authentication
 * Includes CSRF token protection
 */

import { extractCSRFToken, addCSRFToken } from './csrf';

/**
 * Wrapper around fetch that automatically handles 401 responses
 * by redirecting to login page with a message
 * Uses credentials: 'include' to send httpOnly cookies
 * Includes CSRF token for state-changing operations
 */
export async function authenticatedFetch(url, options = {}) {
  // Determine if this is a state-changing operation
  const isStateChanging = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method?.toUpperCase());
  
  // Add CSRF token for state-changing operations
  let requestOptions = options;
  if (isStateChanging) {
    requestOptions = addCSRFToken(options);
  }
  
  // Only set Content-Type for requests with body (state-changing operations)
  const headers = { ...requestOptions.headers };
  if (isStateChanging && requestOptions.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  
  const response = await fetch(url, {
    ...requestOptions,
    credentials: 'include', // Include httpOnly cookies
    headers,
    // Support AbortController signal if provided
    signal: requestOptions.signal,
  });

  // Extract CSRF token from response headers (if present)
  extractCSRFToken(response);

  // Handle 401 Unauthorized - redirect to login
  if (response.status === 401) {
    // Redirect to login with message immediately
    // Only redirect if we're not already on the login page
    if (!window.location.pathname.includes('/login')) {
      const loginUrl = '/login?message=' + encodeURIComponent('Molimo prijavite se ponovo');
      // Use replace to avoid adding to history
      window.location.replace(loginUrl);
    }
    
    // Return a rejected promise to stop execution
    // Use a specific error that components can check for
    const error = new Error('Unauthorized - redirected to login');
    error.redirected = true;
    error.status = 401;
    throw error;
  }

  // Handle 403 Forbidden (CSRF validation failed)
  if (response.status === 403) {
    const data = await response.json().catch(() => ({}));
    if (data.error && data.error.includes('CSRF')) {
      // CSRF token expired, reload page to get new token
      window.location.reload();
      throw new Error('CSRF token expired - page reloaded');
    }
  }

  return response;
}

/**
 * Helper function to get JSON response with 401 handling
 */
export async function authenticatedFetchJson(url, options = {}) {
  const response = await authenticatedFetch(url, options);
  return response.json();
}

/**
 * Helper function to get blob response with 401 handling
 */
export async function authenticatedFetchBlob(url, options = {}) {
  const response = await authenticatedFetch(url, options);
  return response.blob();
}

